import React, { useEffect, useState } from "react";
import { handleGetRequest } from "../../services/GetTemplate";
import styles from "./AdvancedAnalytics.module.scss";
import { Chart } from "primereact/chart";
import {
  FiTrendingUp,
  FiAlertTriangle,
  FiUsers,
  FiBarChart2,
  FiAward,
  FiTarget,
  FiArrowUp,
  FiArrowDown,
  FiPackage,
  FiClock,
  FiDollarSign,
  FiCalendar
} from "react-icons/fi";

const AdvancedAnalyticsDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [performanceData, setPerformanceData] = useState(null);
  const [riskData, setRiskData] = useState(null);
  const [retentionData, setRetentionData] = useState(null);
  const [comparativeData, setComparativeData] = useState(null);
  const [period1, setPeriod1] = useState("10");
  const [period2, setPeriod2] = useState("11");
  const [activeMetric, setActiveMetric] = useState("revenue");
  const [dateRange, setDateRange] = useState("thisMonth");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showCustomRange, setShowCustomRange] = useState(false);

  // Calculate date ranges
  const getDateRangeValues = (range) => {
    const today = new Date();
    const startOfYear = new Date(today.getFullYear(), 0, 1);
    const endOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

    switch (range) {
      case "thisWeek": {
        const dayOfWeek = today.getDay();
        const diff = today.getDate() - dayOfWeek;
        const start = new Date(today.setDate(diff));
        start.setHours(0, 0, 0, 0);
        return {
          start: start.toISOString().split("T")[0],
          end: endOfToday.toISOString().split("T")[0]
        };
      }
      case "thisMonth": {
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        return {
          start: start.toISOString().split("T")[0],
          end: endOfToday.toISOString().split("T")[0]
        };
      }
      case "thisYear": {
        return {
          start: startOfYear.toISOString().split("T")[0],
          end: endOfToday.toISOString().split("T")[0]
        };
      }
      case "custom":
        return { start: startDate, end: endDate };
      default:
        return { start: "", end: "" };
    }
  };

  useEffect(() => {
    if (dateRange !== "custom") {
      const dates = getDateRangeValues(dateRange);
      setStartDate(dates.start);
      setEndDate(dates.end);
    }
  }, [dateRange]);

  useEffect(() => {
    if (startDate && endDate) {
      fetchAllData();
    }
  }, [activeMetric, period1, period2, startDate, endDate]);

  const fetchAllData = async () => {
    try {
      setLoading(true);

      const params = `?limit=10&metric=${activeMetric}&startDate=${startDate}&endDate=${endDate}`;

      const [performance, risk, retention, comparative] = await Promise.all([
        handleGetRequest(`/analytics/insights/performance-scoreboard${params}`),
        handleGetRequest(`/analytics/insights/risk-assessment${params}`),
        handleGetRequest(`/analytics/insights/retention-metrics${params}`),
        handleGetRequest(`/analytics/insights/comparative-analytics?period1=${period1}&period2=${period2}&startDate=${startDate}&endDate=${endDate}`)
      ]);

      // Performance: response.data.data
      if (performance?.data) {
        setPerformanceData(performance.data);
      }

      // Risk: response.data has riskSummary and details directly
      if (risk) {
        setRiskData({
          riskSummary: risk.riskSummary,
          details: risk.details
        });
      }

      // Retention: response.data.retention
      if (retention?.retention) {
        setRetentionData(retention.retention);
      }

      // Comparative: response.data.comparison
      if (comparative?.comparison) {
        setComparativeData(comparative.comparison);
      }
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const months = [
    { label: "January", value: "1" },
    { label: "February", value: "2" },
    { label: "March", value: "3" },
    { label: "April", value: "4" },
    { label: "May", value: "5" },
    { label: "June", value: "6" },
    { label: "July", value: "7" },
    { label: "August", value: "8" },
    { label: "September", value: "9" },
    { label: "October", value: "10" },
    { label: "November", value: "11" },
    { label: "December", value: "12" }
  ];

  // ===== PERFORMANCE DATA PREPARATION =====
  const financialScores = performanceData?.financialScores || [];
  const operationalMetrics = performanceData?.operationalMetrics || [];

  const performanceChartData = financialScores.length > 0 ? {
    labels: financialScores.slice(0, 10).map(item => item.clientName || "Unknown"),
    datasets: [
      {
        label: "Total Revenue",
        data: financialScores.slice(0, 10).map(item => item.totalRevenue || 0),
        backgroundColor: "rgba(102, 126, 234, 0.8)",
        borderColor: "rgba(102, 126, 234, 1)",
        borderWidth: 1
      },
      {
        label: "Total Paid",
        data: financialScores.slice(0, 10).map(item => item.totalPaid || 0),
        backgroundColor: "rgba(16, 185, 129, 0.8)",
        borderColor: "rgba(16, 185, 129, 1)",
        borderWidth: 1
      }
    ]
  } : null;

  const chartOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: { color: "#495057", font: { size: 12 } }
      }
    },
    scales: {
      x: {
        ticks: { color: "#495057" },
        grid: { color: "#ebedef" }
      },
      y: {
        ticks: { color: "#495057" },
        grid: { color: "#ebedef" }
      }
    }
  };

  // ===== RISK DATA CALCULATIONS =====
  const riskSummary = riskData?.riskSummary || {};
  const riskDetails = riskData?.details || [];

  const totalRisk = riskSummary.totalAtRisk || 0;
  const highRiskPercent = totalRisk > 0 ? ((riskSummary.highRisk || 0) / totalRisk) * 100 : 0;
  const mediumRiskPercent = totalRisk > 0 ? ((riskSummary.mediumRisk || 0) / totalRisk) * 100 : 0;
  const lowRiskPercent = totalRisk > 0 ? ((riskSummary.lowRisk || 0) / totalRisk) * 100 : 0;

  // ===== RETENTION DATA =====
  const retentionStats = retentionData || {};
  const retentionRate = parseFloat(retentionStats.retentionRate) || 0;
  const churnRate = parseFloat(retentionStats.churnRate) || 0;

  // ===== COMPARATIVE DATA =====
  const comparison = comparativeData || {};
  const period1Data = comparison.period1 || { totalRevenue: 0, avgInvoice: 0, totalInvoices: 0 };
  const period2Data = comparison.period2 || { totalRevenue: 0, avgInvoice: 0, totalInvoices: 0 };
  const revenueChange = comparison.changes?.revenueChange ? parseFloat(comparison.changes.revenueChange) : 0;
  const invoiceChange = comparison.changes?.invoiceChange ? parseFloat(comparison.changes.invoiceChange) : 0;

  const comparativeChartData = {
    labels: ["Period 1", "Period 2"],
    datasets: [
      {
        label: "Total Revenue",
        data: [period1Data.totalRevenue || 0, period2Data.totalRevenue || 0],
        backgroundColor: ["rgba(102, 126, 234, 0.8)", "rgba(102, 126, 234, 0.5)"],
        borderColor: ["rgba(102, 126, 234, 1)", "rgba(102, 126, 234, 1)"],
        borderWidth: 2
      },
      {
        label: "Total Invoices",
        data: [period1Data.totalInvoices || 0, period2Data.totalInvoices || 0],
        backgroundColor: ["rgba(16, 185, 129, 0.8)", "rgba(16, 185, 129, 0.5)"],
        borderColor: ["rgba(16, 185, 129, 1)", "rgba(16, 185, 129, 1)"],
        borderWidth: 2
      }
    ]
  };

  if (loading) {
    return (
      <div className={styles.dashboard}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading Advanced Analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.dashboard}>

      {/* ===== DATE RANGE FILTER ===== */}
      <div className={styles.filterBar}>
        <div className={styles.filterSection}>
          <FiCalendar className={styles.filterIcon} />
          <div className={styles.dateRangeButtons}>
            <button
              className={`${styles.rangeBtn} ${dateRange === "thisWeek" ? styles.active : ""}`}
              onClick={() => setDateRange("thisWeek")}
            >
              📅 This Week
            </button>
            <button
              className={`${styles.rangeBtn} ${dateRange === "thisMonth" ? styles.active : ""}`}
              onClick={() => setDateRange("thisMonth")}
            >
              📆 This Month
            </button>
            <button
              className={`${styles.rangeBtn} ${dateRange === "thisYear" ? styles.active : ""}`}
              onClick={() => setDateRange("thisYear")}
            >
              📋 This Year
            </button>
            <button
              className={`${styles.rangeBtn} ${dateRange === "custom" ? styles.active : ""}`}
              onClick={() => {
                setDateRange("custom");
                setShowCustomRange(!showCustomRange);
              }}
            >
              📍 Custom
            </button>
          </div>
        </div>

        {/* Custom Date Range */}
        {showCustomRange && (
          <div className={styles.customDateRange}>
            <div className={styles.dateInputGroup}>
              <label>Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className={styles.dateInput}
              />
            </div>
            <div className={styles.dateInputGroup}>
              <label>End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className={styles.dateInput}
              />
            </div>
            <button
              className={styles.applyBtn}
              onClick={() => fetchAllData()}
            >
              Apply Filter
            </button>
          </div>
        )}

        {/* Display current date range */}
        <div className={styles.currentRange}>
          <span>📊 Period: <strong>{startDate}</strong> to <strong>{endDate}</strong></span>
        </div>
      </div>

      {/* ===== PERFORMANCE SCOREBOARD ===== */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>🏆 Performance Scoreboard</h2>
          <div className={styles.metricFilter}>
            <button
              className={`${styles.filterBtn} ${activeMetric === "revenue" ? styles.active : ""}`}
              onClick={() => setActiveMetric("revenue")}
            >
              💰 Revenue
            </button>
            <button
              className={`${styles.filterBtn} ${activeMetric === "paid" ? styles.active : ""}`}
              onClick={() => setActiveMetric("paid")}
            >
              ✅ Paid
            </button>
            <button
              className={`${styles.filterBtn} ${activeMetric === "pending" ? styles.active : ""}`}
              onClick={() => setActiveMetric("pending")}
            >
              ⏳ Pending
            </button>
          </div>
        </div>

        <div className={styles.performanceGrid}>
          {/* Performance Chart */}
          <div className={styles.card}>
            <h3>Top 10 Users by {activeMetric}</h3>
            {performanceChartData && financialScores.length > 0 ? (
              <Chart type="bar" data={performanceChartData} options={chartOptions} height={400} />
            ) : (
              <div className={styles.empty}>
                {!financialScores.length ? "No performance data available" : "Loading chart..."}
              </div>
            )}
          </div>

          {/* Top Packages */}
          <div className={styles.card}>
            <h3>📦 Top Packages</h3>
            {operationalMetrics.length > 0 ? (
              <div className={styles.packageList}>
                {operationalMetrics.slice(0, 8).map((pkg, idx) => (
                  <div key={idx} className={styles.packageItem}>
                    <div className={styles.pkgRank}>{idx + 1}</div>
                    <div className={styles.pkgInfo}>
                      <p className={styles.pkgName}>{pkg._id || "Unknown"}</p>
                      <p className={styles.pkgCount}>{pkg.totalPackages || 0} packages</p>
                    </div>
                    <div className={styles.pkgMeter}>
                      <div
                        className={styles.pkgBar}
                        style={{
                          width: `${((pkg.totalPackages || 0) / Math.max(operationalMetrics[0]?.totalPackages || 1, 1)) * 100}%`
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.empty}>No package data available</div>
            )}
          </div>
        </div>

        {/* Performance Rankings Table */}
        <div className={`${styles.card} ${styles.fullWidth}`}>
          <h3>📊 Detailed Rankings</h3>
          {financialScores.length > 0 ? (
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>User Name</th>
                    <th>Total Revenue</th>
                    <th>Total Paid</th>
                    <th>Pending</th>
                    <th>Payment Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {financialScores.map((user, idx) => (
                    <tr key={user._id || idx}>
                      <td className={styles.rank}>#{idx + 1}</td>
                      <td className={styles.userName}>{user.clientName || "Unknown"}</td>
                      <td className={styles.currency}>
                        ${((user.totalRevenue || 0).toLocaleString(undefined, { maximumFractionDigits: 2 }))}
                      </td>
                      <td className={styles.success}>
                        ${((user.totalPaid || 0).toLocaleString(undefined, { maximumFractionDigits: 2 }))}
                      </td>
                      <td className={styles.warning}>
                        ${((user.totalPending || 0).toLocaleString(undefined, { maximumFractionDigits: 2 }))}
                      </td>
                      <td className={styles.rate}>{(user.paymentRate || 0).toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={styles.empty}>No detailed rankings available</div>
          )}
        </div>
      </section>

      {/* ===== RISK ASSESSMENT ===== */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>⚠️ Risk Assessment</h2>
          <p>Overdue invoices older than 30 days</p>
        </div>

        {/* Risk Cards */}
        <div className={styles.riskGrid}>
          <div className={`${styles.riskCard} ${styles.highRisk}`}>
            <div className={styles.riskIcon}>🔴</div>
            <div className={styles.riskContent}>
              <p className={styles.riskLabel}>High Risk</p>
              <p className={styles.riskValue}>{riskSummary.highRisk ?? 0}</p>
              <p className={styles.riskPercent}>{highRiskPercent.toFixed(1)}% of total</p>
              <p className={styles.riskDesc}>Over $10,000 overdue</p>
            </div>
          </div>

          <div className={`${styles.riskCard} ${styles.mediumRisk}`}>
            <div className={styles.riskIcon}>🟡</div>
            <div className={styles.riskContent}>
              <p className={styles.riskLabel}>Medium Risk</p>
              <p className={styles.riskValue}>{riskSummary.mediumRisk ?? 0}</p>
              <p className={styles.riskPercent}>{mediumRiskPercent.toFixed(1)}% of total</p>
              <p className={styles.riskDesc}>$5,000 - $10,000 overdue</p>
            </div>
          </div>

          <div className={`${styles.riskCard} ${styles.lowRisk}`}>
            <div className={styles.riskIcon}>🟢</div>
            <div className={styles.riskContent}>
              <p className={styles.riskLabel}>Low Risk</p>
              <p className={styles.riskValue}>{riskSummary.lowRisk ?? 0}</p>
              <p className={styles.riskPercent}>{lowRiskPercent.toFixed(1)}% of total</p>
              <p className={styles.riskDesc}>Under $5,000 overdue</p>
            </div>
          </div>

          <div className={`${styles.riskCard} ${styles.total}`}>
            <div className={styles.riskIcon}>💰</div>
            <div className={styles.riskContent}>
              <p className={styles.riskLabel}>Total at Risk</p>
              <p className={styles.riskValue}>
                ${((riskSummary.estimatedUncollectable ?? 0).toLocaleString())}
              </p>
              <p className={styles.riskPercent}>{riskSummary.totalAtRisk ?? 0} invoices</p>
              <p className={styles.riskDesc}>Estimated uncollectable</p>
            </div>
          </div>
        </div>

        {/* Risk Distribution */}
        <div className={`${styles.card} ${styles.fullWidth}`}>
          <h3>Risk Distribution</h3>
          {totalRisk > 0 ? (
            <div className={styles.riskBars}>
              <div className={styles.riskBarContainer}>
                <div className={styles.barLabel}>High Risk</div>
                <div className={styles.barTrack}>
                  <div
                    className={`${styles.bar} ${styles.barHigh}`}
                    style={{ width: `${Math.max(highRiskPercent, 5)}%` }}
                  >
                    <span className={styles.barValue}>{highRiskPercent.toFixed(1)}%</span>
                  </div>
                </div>
              </div>

              <div className={styles.riskBarContainer}>
                <div className={styles.barLabel}>Medium Risk</div>
                <div className={styles.barTrack}>
                  <div
                    className={`${styles.bar} ${styles.barMedium}`}
                    style={{ width: `${Math.max(mediumRiskPercent, 5)}%` }}
                  >
                    <span className={styles.barValue}>{mediumRiskPercent.toFixed(1)}%</span>
                  </div>
                </div>
              </div>

              <div className={styles.riskBarContainer}>
                <div className={styles.barLabel}>Low Risk</div>
                <div className={styles.barTrack}>
                  <div
                    className={`${styles.bar} ${styles.barLow}`}
                    style={{ width: `${Math.max(lowRiskPercent, 5)}%` }}
                  >
                    <span className={styles.barValue}>{lowRiskPercent.toFixed(1)}%</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.empty}>No risk data available</div>
          )}
        </div>

        {/* Top At-Risk Clients */}
        <div className={`${styles.card} ${styles.fullWidth}`}>
          <h3>Top 20 At-Risk Clients</h3>
          {riskDetails && riskDetails.length > 0 ? (
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Total Overdue</th>
                    <th>Invoice Count</th>
                    <th>Oldest Invoice</th>
                    <th>Risk Level</th>
                  </tr>
                </thead>
                <tbody>
                  {riskDetails.map((item, idx) => {
                    let riskLevel = "Low";
                    let riskClass = "lowRisk";
                    if ((item.totalOverdue || 0) > 10000) {
                      riskLevel = "High";
                      riskClass = "highRisk";
                    } else if ((item.totalOverdue || 0) > 5000) {
                      riskLevel = "Medium";
                      riskClass = "mediumRisk";
                    }

                    const daysOverdue = Math.floor(
                      (new Date() - new Date(item.oldestInvoice)) / (1000 * 60 * 60 * 24)
                    );

                    return (
                      <tr key={idx}>
                        <td className={styles.clientCell}>{item.clientName || item._id || "Unknown"}</td>
                        <td className={styles.amount}>
                          ${((item.totalOverdue || 0).toLocaleString())}
                        </td>
                        <td className={styles.count}>{item.invoiceCount || 0}</td>
                        <td className={styles.daysOld}>{daysOverdue} days old</td>
                        <td>
                          <span className={`${styles.badge} ${styles[riskClass]}`}>
                            {riskLevel}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={styles.empty}>No at-risk clients</div>
          )}
        </div>
      </section>

      {/* ===== RETENTION METRICS ===== */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>👥 Retention Metrics</h2>
          <p>User retention and churn analysis</p>
        </div>

        {/* Retention KPI Cards */}
        <div className={styles.retentionGrid}>
          <div className={`${styles.metricCard} ${styles.primary}`}>
            <div className={styles.metricIcon}><FiUsers /></div>
            <div className={styles.metricContent}>
              <p className={styles.metricLabel}>Active Users (30 Days)</p>
              <p className={styles.metricValue}>{retentionStats.activeUsers30Days ?? 0}</p>
              <p className={styles.metricDesc}>New users this month</p>
            </div>
          </div>

          <div className={`${styles.metricCard} ${styles.success}`}>
            <div className={styles.metricIcon}><FiTarget /></div>
            <div className={styles.metricContent}>
              <p className={styles.metricLabel}>Total Active Users</p>
              <p className={styles.metricValue}>{retentionStats.totalActive ?? 0}</p>
              <p className={styles.metricDesc}>Currently active</p>
            </div>
          </div>

          <div className={`${styles.metricCard} ${styles.warning}`}>
            <div className={styles.metricIcon}><FiAlertTriangle /></div>
            <div className={styles.metricContent}>
              <p className={styles.metricLabel}>Churned Users</p>
              <p className={styles.metricValue}>{retentionStats.churnedUsers ?? 0}</p>
              <p className={styles.metricDesc}>Last 60-90 days</p>
            </div>
          </div>

          <div className={`${styles.metricCard} ${styles.info}`}>
            <div className={styles.metricIcon}><FiTrendingUp /></div>
            <div className={styles.metricContent}>
              <p className={styles.metricLabel}>Retention Rate</p>
              <p className={styles.metricValue}>{retentionRate.toFixed(1)}%</p>
              <p className={styles.metricDesc}>Users retained</p>
            </div>
          </div>
        </div>

        {/* Retention Visualization */}
        <div className={styles.retentionVisualization}>
          <div className={styles.card}>
            <h3>Retention Rate Breakdown</h3>
            <div className={styles.pieChart}>
              <div className={styles.pieContainer}>
                <svg viewBox="0 0 100 100" className={styles.svg}>
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    fill="none"
                    stroke="#10b981"
                    strokeWidth="30"
                    strokeDasharray={`${Math.max(retentionRate, 0.1) * 2.83} 283`}
                    transform="rotate(-90 50 50)"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    fill="none"
                    stroke="#ef4444"
                    strokeWidth="30"
                    strokeDasharray={`${Math.max(churnRate, 0.1) * 2.83} 283`}
                    strokeDashoffset={-Math.max(retentionRate, 0.1) * 2.83}
                    transform="rotate(-90 50 50)"
                  />
                </svg>
                <div className={styles.pieCenter}>
                  <p className={styles.pieLabel}>Overall</p>
                  <p className={styles.pieValue}>{retentionRate.toFixed(1)}%</p>
                </div>
              </div>
            </div>
            <div className={styles.legend}>
              <div className={styles.legendItem}>
                <div className={`${styles.legendColor} ${styles.retained}`}></div>
                <span>Retained: {retentionRate.toFixed(1)}%</span>
              </div>
              <div className={styles.legendItem}>
                <div className={`${styles.legendColor} ${styles.churned}`}></div>
                <span>Churned: {churnRate.toFixed(1)}%</span>
              </div>
            </div>
          </div>

          <div className={styles.card}>
            <h3>Key Metrics</h3>
            <div className={styles.statsList}>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Retention Rate</span>
                <span className={styles.statValue}>{retentionRate.toFixed(2)}%</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Churn Rate</span>
                <span className={styles.statValue}>{churnRate.toFixed(2)}%</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Total Active Users</span>
                <span className={styles.statValue}>{retentionStats.totalActive ?? 0}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>New Users (30d)</span>
                <span className={styles.statValue}>{retentionStats.activeUsers30Days ?? 0}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Churned Users (60-90d)</span>
                <span className={styles.statValue}>{retentionStats.churnedUsers ?? 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Health Status */}
        <div className={`${styles.card} ${styles.fullWidth}`}>
          <h3>📊 Retention Health</h3>
          <div className={styles.healthStatus}>
            <div className={`${styles.statusBar} ${getHealthStatus(retentionRate)}`}></div>
            <p className={styles.healthText}>{getHealthMessage(retentionRate)}</p>
          </div>
        </div>
      </section>

      {/* ===== COMPARATIVE ANALYTICS ===== */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>📈 Comparative Analytics</h2>
          <p>Compare metrics across two time periods</p>
        </div>

        {/* Period Selectors */}
        <div className={styles.periodSelectors}>
          <div className={styles.periodGroup}>
            <label>Period 1</label>
            <select value={period1} onChange={(e) => setPeriod1(e.target.value)} className={styles.select}>
              {months.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
          <div className={styles.vsLabel}>vs</div>
          <div className={styles.periodGroup}>
            <label>Period 2</label>
            <select value={period2} onChange={(e) => setPeriod2(e.target.value)} className={styles.select}>
              {months.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Comparison Chart */}
        <div className={`${styles.card} ${styles.fullWidth}`}>
          <h3>Revenue & Invoice Comparison</h3>
          <Chart type="bar" data={comparativeChartData} options={chartOptions} height={300} />
        </div>

        {/* Comparison Cards */}
        <div className={styles.comparisonGrid}>
          <div className={styles.card}>
            <h4>Period 1</h4>
            <div className={styles.comparisonStats}>
              <div className={styles.stat}>
                <span className={styles.label}>Total Revenue</span>
                <span className={styles.value}>
                  ${((period1Data.totalRevenue ?? 0).toLocaleString())}
                </span>
              </div>
              <div className={styles.stat}>
                <span className={styles.label}>Avg Invoice</span>
                <span className={styles.value}>
                  ${((period1Data.avgInvoice ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 }))}
                </span>
              </div>
              <div className={styles.stat}>
                <span className={styles.label}>Total Invoices</span>
                <span className={styles.value}>{period1Data.totalInvoices ?? 0}</span>
              </div>
            </div>
          </div>

          <div className={styles.card}>
            <h4>Period-over-Period Change</h4>
            <div className={styles.changeStats}>
              <div className={`${styles.changeStat} ${revenueChange >= 0 ? styles.positive : styles.negative}`}>
                <span className={styles.label}>Revenue Change</span>
                <span className={styles.value}>
                  {revenueChange >= 0 ? <FiArrowUp /> : <FiArrowDown />}
                  {Math.abs(revenueChange).toFixed(2)}%
                </span>
              </div>
              <div className={`${styles.changeStat} ${invoiceChange >= 0 ? styles.positive : styles.negative}`}>
                <span className={styles.label}>Invoice Change</span>
                <span className={styles.value}>
                  {invoiceChange >= 0 ? <FiArrowUp /> : <FiArrowDown />}
                  {Math.abs(invoiceChange).toFixed(2)}%
                </span>
              </div>
            </div>
          </div>

          <div className={styles.card}>
            <h4>Period 2</h4>
            <div className={styles.comparisonStats}>
              <div className={styles.stat}>
                <span className={styles.label}>Total Revenue</span>
                <span className={styles.value}>
                  ${((period2Data.totalRevenue ?? 0).toLocaleString())}
                </span>
              </div>
              <div className={styles.stat}>
                <span className={styles.label}>Avg Invoice</span>
                <span className={styles.value}>
                  ${((period2Data.avgInvoice ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 }))}
                </span>
              </div>
              <div className={styles.stat}>
                <span className={styles.label}>Total Invoices</span>
                <span className={styles.value}>{period2Data.totalInvoices ?? 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Insights */}
        <div className={`${styles.card} ${styles.fullWidth}`}>
          <h3>💡 Key Insights</h3>
          <div className={styles.insightsList}>
            {revenueChange > 0 ? (
              <div className={styles.insight + " " + styles.positive}>
                ✅ Revenue increased by {Math.abs(revenueChange).toFixed(2)}% from Period 1 to Period 2
              </div>
            ) : revenueChange < 0 ? (
              <div className={styles.insight + " " + styles.negative}>
                ⚠️ Revenue decreased by {Math.abs(revenueChange).toFixed(2)}% from Period 1 to Period 2
              </div>
            ) : (
              <div className={styles.insight}>
                📊 Revenue remained stable between periods
              </div>
            )}

            {invoiceChange > 0 ? (
              <div className={styles.insight + " " + styles.positive}>
                ✅ Invoice volume increased by {Math.abs(invoiceChange).toFixed(2)}%
              </div>
            ) : invoiceChange < 0 ? (
              <div className={styles.insight + " " + styles.negative}>
                ⚠️ Invoice volume decreased by {Math.abs(invoiceChange).toFixed(2)}%
              </div>
            ) : (
              <div className={styles.insight}>
                📊 Invoice volume remained stable
              </div>
            )}

            {period2Data.totalInvoices > 0 ? (
              <div className={styles.insight}>
                📊 Average invoice value in Period 2: ${((period2Data.avgInvoice ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 }))}
              </div>
            ) : (
              <div className={styles.insight}>
                📊 No invoice data for Period 2
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

const getHealthStatus = (rate) => {
  if (rate >= 80) return "excellent";
  if (rate >= 60) return "good";
  if (rate >= 40) return "fair";
  return "poor";
};

const getHealthMessage = (rate) => {
  if (rate >= 80) return "✅ Excellent retention rate! Keep up the momentum.";
  if (rate >= 60) return "👍 Good retention rate. Room for improvement.";
  if (rate >= 40) return "⚠️ Fair retention rate. Focus on user engagement.";
  return "🔴 Poor retention rate. Immediate action needed.";
};

export default AdvancedAnalyticsDashboard;